import type { CategoricalSlot } from "./categorical-color";

/**
 * The static tones a badge can wear when its colour is a semantic status
 * rather than an id/key. `Tag`'s `TagTone` vocabulary plus `purple` — see the
 * `TODO:` on `BadgeColor` below for why the two are not simply one type.
 *
 * `purple` (SR-342 P3 / D-1) is a badge-only tone: the outbound task statuses
 * the PM specified want a purple "Open", and no `Tag` call site asks for one.
 * It resolves to the `purple-container` / `on-purple-container` role pair
 * like every other tone here — a *static* pair, deliberately not
 * `categorical-4`, which happens to be the same hue but is id-derived colour
 * by construction (`categorical-color.ts`) and so is not a status vocabulary.
 */
export type BadgeTone =
  "neutral" | "info" | "success" | "warning" | "danger" | "accent" | "purple";

/**
 * `Badge`/`DismissibleChip`'s colour axis (SR-301 phase 8 doc §4c): a badge
 * is either a fixed semantic tone (`static`, `Tag`'s vocabulary) or an
 * id-derived categorical slot (`categorical`, B1). Both resolve to a single
 * class string so callers never branch on which one they have.
 *
 * TODO: `Badge` and `Tag` plausibly collapse into one component — both
 * resolve a tone to a paired container/on-container role, and the
 * `static` arm here is `TagTone` by another name. The real difference left
 * is geometry (`Tag`'s `4px`/`radius: 4px` vs `Badge`'s Q22-majority
 * `3px 10px`/`radius-full`) and this `categorical` arm, which `Tag` has no
 * way to express. Not merged here — SR-301 mandate #6: this is a note for
 * whichever phase actually reconciles them, not a decision to act on now.
 */
export type BadgeColor =
  | { mode: "static"; tone: BadgeTone }
  | { mode: "categorical"; slot: CategoricalSlot };

/*
 * THE TWO TABLES BELOW ARE THE TS<->CSS CONTRACT, AND IT IS A CLASS-NAME
 * CONTRACT — NOT A VARIABLE ONE.
 *
 * An earlier revision of this file returned `{ color: "var(--pallet-role-…)" }`
 * for an inline `style`, which pushed TypeScript across the boundary into
 * resolving custom properties by hand. Nothing checked those strings: a
 * typo'd or retired role produced a badge with no colour at all and no error
 * anywhere in the build. Class names are what SR-301 built a vocabulary for,
 * so this resolves to class names and lets that vocabulary's guard rail do
 * the checking.
 *
 * Two independent build-time guarantees hold this together, and both are
 * load-bearing:
 *
 * 1. `Record<BadgeTone, string>` / `Record<CategoricalSlot, string>` are
 *    exhaustive by construction. Adding a member to either union is a `tsc`
 *    error here, at the exact line that has to answer for it. This is the
 *    same idiom `STATUS_TONES` / `PLAN_STATUS_TONES` use outbound.
 *
 * 2. Every class name below is validated against pallet's real Tailwind entry
 *    CSS by `better-tailwindcss/no-unknown-classes` (SR-301 phase 6.5). It
 *    fires here because `eslint.config.ts` gives the rule a `variables`
 *    selector matching `*_CLASSES` with an `objectValues` matcher — the
 *    plugin's DEFAULTS DO NOT SCAN OBJECT VALUES, only whole-string variable
 *    initialisers, so **renaming these consts off the `_CLASSES` suffix
 *    silently disables the check**. Verified live: with the selector in place
 *    a bogus `bg-nope-500` in either table is an `npm run eslint` error; with
 *    the const renamed it is not.
 *
 * The roles these classes are generated from are wired into the Tailwind
 * namespace in `styles/theme.css` — a role that exists in `tokens.css` but is
 * missing from `theme.css` generates no utility, and (1) will not catch that
 * while (2) will.
 */

/** Semantic tone -> its `X-container` / `on-X-container` utility pair. */
const STATIC_TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: "bg-neutral-container text-on-neutral-container",
  info: "bg-info-container text-on-info-container",
  success: "bg-success-container text-on-success-container",
  warning: "bg-warning-container text-on-warning-container",
  danger: "bg-danger-container text-on-danger-container",
  accent: "bg-accent-container text-on-accent-container",
  purple: "bg-purple-container text-on-purple-container",
};

/**
 * Categorical slot -> its utility pair. Spelled out per slot rather than
 * built as `` `text-categorical-${slot}` `` on purpose: an interpolated class
 * name is skipped by `no-unknown-classes` by design (the plugin's
 * `isConcatenatedClass` bails on any literal carrying a `${`), so the
 * template-literal form would compile, lint clean, and generate nothing.
 *
 * Note the inverted grammar against `STATIC_TONE_CLASSES` — bare name is the
 * ink, `-container` is the paint. See the comment on the categorical block in
 * `styles/theme.css`, and SR-342 §10 Q13.
 */
const CATEGORICAL_SLOT_CLASSES: Record<CategoricalSlot, string> = {
  1: "text-categorical-1 bg-categorical-1-container",
  2: "text-categorical-2 bg-categorical-2-container",
  3: "text-categorical-3 bg-categorical-3-container",
  4: "text-categorical-4 bg-categorical-4-container",
  5: "text-categorical-5 bg-categorical-5-container",
  6: "text-categorical-6 bg-categorical-6-container",
};

/** Resolves either arm of `BadgeColor` to the pallet utility classes a
 *  badge-shaped root wears. Compose it with `clsx`. */
export function badgeColorClass(color: BadgeColor): string {
  return color.mode === "categorical"
    ? CATEGORICAL_SLOT_CLASSES[color.slot]
    : STATIC_TONE_CLASSES[color.tone];
}
